import { useState } from 'react'

import { useQuery } from '@percent/utility'
import type { EffectiveRolesResponse } from '@percent/domain/iam'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import {
  POSTHOG_FEATURE_FLAGS,
  usePostHogFeatureFlag
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { usePermissions } from '@percent/admin-dashboard/common/hooks/usePermissions/usePermissions'

// The roles an account or group holds and why, behind ad_effective_roles. Asked
// alongside assigned-domains rather than inside it, so the permissions list
// never waits on it: until it answers, inherited roles just have no marker.
export function useEffectiveRoles(subject: 'accounts' | 'groups', id: string | undefined) {
  const { iamService } = useServices()
  const isEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_EFFECTIVE_ROLES)
  const { userHasPermissions } = usePermissions()
  // Eventual until one of this subject's roles is changed here; strong after,
  // so the reasons read back the change rather than what OpenFGA had cached.
  // Each change is its own query, so changes made close together each get a
  // read that starts after them.
  const [changes, setChanges] = useState<{ id?: string; count: number }>({ count: 0 })
  const changeCount = id && changes.id === id ? changes.count : 0
  const consistencyLevel = changeCount > 0 ? 'strong' : 'eventual'

  const { data, isLoading, errorMessage } = useQuery(
    ['effectiveRoles', subject, id, changeCount],
    iamService.getEffectiveRoles,
    { subject, id: id ?? '', consistencyLevel },
    {
      enabled: isEnabled && !!id,
      // Kept only while the same subject is asked again: another account's
      // reasons must never show against this one's roles.
      placeholderData: (
        previous: EffectiveRolesResponse | undefined,
        previousQuery: { queryKey: readonly unknown[] } | undefined
      ) => (previousQuery?.queryKey[1] === subject && previousQuery.queryKey[2] === id ? previous : undefined)
    }
  )

  return {
    isEnabled,
    effectiveDomains: data ?? undefined,
    isLoading: isEnabled && isLoading,
    errorMessage,
    // Group names link to the group's page only for a viewer who can open it
    getGroupHref: userHasPermissions('groups:can_view')
      ? (groupId: string) => `/accounts/groups/${groupId}`
      : undefined,
    refresh: () => {
      if (isEnabled && id) setChanges(previous => ({ id, count: previous.id === id ? previous.count + 1 : 1 }))
    }
  }
}
